import { Controller } from "@hotwired/stimulus"
import { createLogger } from "../lib/logger"
import { getI18nMessage } from "../lib/i18n_helper"
import { showError, showSuccess } from "../lib/platform_toast"

/**
 * Listing Compare Controller
 * Manages compare selection state for listings
 *
 * Features:
 *   - Toggle compare selection on listing cards
 *   - Show floating compare bar when items selected
 *   - Navigate to compare page with selected IDs
 *
 * Usage:
 *   <div data-controller="listing-compare"
 *        data-listing-compare-max-value="4"
 *        data-listing-compare-compare-url-value="/listings/compare">
 *     <button data-listing-compare-target="toggle"
 *             data-listing-id="123"
 *             data-action="click->listing-compare#toggleSelection">
 *       Compare
 *     </button>
 *     <div data-listing-compare-target="bar" class="hidden">
 *       <span data-listing-compare-target="count">0</span> selected
 *       <button data-action="click->listing-compare#openComparison">Compare</button>
 *       <button data-action="click->listing-compare#clearSelection">Clear</button>
 *     </div>
 *   </div>
 */
const log = createLogger('ListingCompare')

// Filled selected state for the icon-only compare toggle (important: overrides the base white/charcoal classes).
const SELECTED_CLASSES = ["!bg-crust-600", "!text-white", "!border-crust-600"]

export default class extends Controller {
  static targets = ["toggle", "bar", "count"]
  static values = {
    max: { type: Number, default: 4 },
    compareUrl: { type: String, default: "/listings/compare" }
  }

  connect() {
    // Initialize selection from sessionStorage (persist across page navigations)
    this.selectedIds = new Set(this.loadFromStorage())
    this.updateUI()
    log.debug("ListingCompare connected", { selected: this.selectedIds.size })
  }

  /**
   * Toggle listing selection for comparison
   */
  toggleSelection(event) {
    const button = event.currentTarget
    const listingId = button.dataset.listingId

    if (!listingId) {
      log.error("Missing listing-id on toggle button")
      return
    }

    if (this.selectedIds.has(listingId)) {
      // Deselect
      this.selectedIds.delete(listingId)
      button.classList.remove(...SELECTED_CLASSES)
      log.debug("Listing deselected", { listingId })
    } else {
      // Check max limit
      if (this.selectedIds.size >= this.maxValue) {
        const errorMsg = getI18nMessage(
          this.element,
          'maxReached',
          `最多可選擇 ${this.maxValue} 間房源進行比較`
        )
        showError(errorMsg)
        return
      }

      // Select
      this.selectedIds.add(listingId)
      button.classList.add(...SELECTED_CLASSES)
      log.debug("Listing selected", { listingId })
    }

    this.saveToStorage()
    this.updateUI()
  }

  /**
   * Navigate to compare page with selected listings
   */
  openComparison() {
    if (this.selectedIds.size < 2) {
      const errorMsg = getI18nMessage(
        this.element,
        'minRequired',
        '請選擇至少 2 間房源進行比較'
      )
      showError(errorMsg)
      return
    }

    // Build URL with IDs
    const params = new URLSearchParams()
    this.selectedIds.forEach(id => params.append('ids[]', id))
    const url = `${this.compareUrlValue}?${params.toString()}`

    log.info("Opening comparison", { count: this.selectedIds.size, url })
    window.location.href = url
  }

  /**
   * Clear all selections
   */
  clearSelection() {
    this.selectedIds.clear()
    this.saveToStorage()
    this.updateUI()

    // Remove visual selection from all toggle buttons
    this.toggleTargets.forEach(button => {
      button.classList.remove(...SELECTED_CLASSES)
    })

    log.debug("Selection cleared")
  }

  /**
   * Update UI based on selection state
   */
  updateUI() {
    const count = this.selectedIds.size

    // Update count display
    if (this.hasCountTarget) {
      this.countTarget.textContent = count
    }

    // Show/hide compare bar
    if (this.hasBarTarget) {
      this.barTarget.classList.toggle("hidden", count === 0)
    }

    // Update toggle button states
    this.toggleTargets.forEach(button => {
      const listingId = button.dataset.listingId
      const isSelected = this.selectedIds.has(listingId)
      SELECTED_CLASSES.forEach(c => button.classList.toggle(c, isSelected))
    })
  }

  /**
   * Save selection to sessionStorage
   */
  saveToStorage() {
    try {
      sessionStorage.setItem('listing_compare_ids', JSON.stringify([...this.selectedIds]))
    } catch (e) {
      log.warn("Failed to save to sessionStorage", e)
    }
  }

  /**
   * Load selection from sessionStorage
   */
  loadFromStorage() {
    try {
      const stored = sessionStorage.getItem('listing_compare_ids')
      return stored ? JSON.parse(stored) : []
    } catch (e) {
      log.warn("Failed to load from sessionStorage", e)
      return []
    }
  }
}
